import { useEffect, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import { Markdown } from 'tiptap-markdown';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';

interface UseDocumentEditorProps {
  initialContent: string;
  onUpdate?: () => void;
  editable?: boolean;
}

export function useDocumentEditor({ initialContent, onUpdate, editable = true }: UseDocumentEditorProps) {
  const lastLoadedContent = useRef<string>('');
  const suppressUpdate = useRef(false);

  const editor = useEditor({
    editable,
    onUpdate: () => {
      if (suppressUpdate.current) return;
      onUpdate?.();
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none min-h-[300px] px-4 py-3${!editable ? ' cursor-default' : ''}`,
      },
    },
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Highlight.configure({ multicolor: true }),
      Markdown.configure({
        html: true,
        breaks: true,
        linkify: true,
        tightLists: true,
        bulletListMarker: '-',
        tightListClass: 'tight',
        transformPastedText: true,
        transformCopiedText: true,
      }),
      Placeholder.configure({
        placeholder: 'Report content will appear here...',
      }),
    ],
  });

  // Load content when editor is ready and content changes — suppress onUpdate during load
  useEffect(() => {
    if (editor && initialContent && initialContent !== lastLoadedContent.current) {
      suppressUpdate.current = true;
      editor.commands.setContent(initialContent, false);
      lastLoadedContent.current = initialContent;
      // Allow onUpdate again on next tick after React re-renders
      requestAnimationFrame(() => { suppressUpdate.current = false; });
    }
  }, [editor, initialContent]);

  // Update editable state when it changes
  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  return editor;
}
